/**
 * RestaurantAboutSection Component
 *
 * Consolidated About section (label "At a Glance") for the restaurant profile.
 * Collapsed state shows AtAGlance (categories, cuisines, highlights).
 * Expanded state adds unified toggle and keyword groups from business config.
 *
 * Display Logic:
 * - Enhanced State: Has BusinessConfig with 1+ category AND (3+ keywords OR 1+ toggles)
 * - Basic State: Falls back to vibes or shows nothing if no data available
 */

import { useMemo, useState } from 'react';
import {
  Box,
  Collapse,
  HStack,
  VStack,
  Text,
} from '@chakra-ui/react';
import { motion, Variants } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { Restaurant } from '@/types/restaurant';
import { ModuleLabel } from '@/components/display';
import { AtAGlance } from './AtAGlance';
import { UnifiedChip } from './UnifiedChip';
import { ALLERGY_ACCOMMODATION_KEYS } from './allergyAccommodationOptions';
import { getIconComponent } from '@/utils/iconUtils';
import {
  useUIMetadata,
  getKeywordDisplayName,
  getKeywordsByGroup,
  getKeywordOption,
  getVibeLabel,
  getVibeGradient,
} from '@/api/queries/useConfigQueries';

// Motion components
const MotionBox = motion.create(Box);

// Animation variants
const sectionVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

const containerVariants: Variants = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const vibeVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.8,
    y: 8,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 500,
      damping: 30,
    },
  },
};

// =============================================================================
// Unified Section Configuration
// =============================================================================

/** Pre-processed tag data for UnifiedChip rendering (toggles and keywords) */
interface TagItem {
  key: string;
  label: string;
  iconName: string | null | undefined;
  group: string;
}

/** A single section within a visual group (toggle or keyword) */
interface SectionConfig {
  key: string;
  label: string;
  source: 'toggles' | 'keywords';
}

/** A visual group containing multiple sections */
interface GroupConfig {
  sections: SectionConfig[];
}

/**
 * Unified section groups for the expanded view.
 * 4 visual groups with extra spacing between them; empty groups are skipped.
 */
const UNIFIED_SECTION_GROUPS: GroupConfig[] = [
  // Group A — Operational Toggles
  {
    sections: [
      { key: 'service', label: 'Service Options', source: 'toggles' },
      { key: 'menu_times', label: 'Menu Options', source: 'toggles' },
      { key: 'accessibility', label: 'Accessibility', source: 'toggles' },
      { key: 'family', label: 'Family Options', source: 'toggles' },
    ],
  },
  // Group B — Experience Keywords
  {
    sections: [
      { key: 'views_setting', label: 'Views & Setting', source: 'keywords' },
      { key: 'ambiance', label: 'Ambiance', source: 'keywords' },
      { key: 'seating_style', label: 'Seating Style', source: 'keywords' },
      { key: 'entertainment', label: 'Entertainment', source: 'keywords' },
    ],
  },
  // Group C — Food & Drink Keywords
  {
    sections: [
      { key: 'dietary_accommodations', label: 'Dietary Accommodations', source: 'keywords' },
      { key: 'menu_specialties', label: 'Menu Specialties', source: 'keywords' },
      { key: 'beverages', label: 'Drinks', source: 'keywords' },
    ],
  },
  // Group D — Logistics
  {
    sections: [
      { key: 'parking', label: 'Parking', source: 'keywords' },
      { key: 'tech_payment', label: 'Technology & Payment', source: 'toggles' },
      { key: 'pricing', label: 'Pricing Fees', source: 'toggles' },
    ],
  },
];

// =============================================================================
// Helpers
// =============================================================================

/**
 * Build the display label for a toggle, appending amount for pricing toggles.
 */
const getToggleLabel = (toggle: NonNullable<Restaurant['toggles']>[number]): string => {
  if (toggle.amount && toggle.supportsAmount) {
    const unit = toggle.amountUnit ? `/${toggle.amountUnit}` : '';
    if (toggle.amountType === 'percentage') {
      return `${toggle.displayName}: ${toggle.amount}%${unit}`;
    }
    return `${toggle.displayName}: $${toggle.amount}${unit}`;
  }
  return toggle.displayName;
};

// =============================================================================
// Props
// =============================================================================

export interface RestaurantAboutSectionProps {
  /** Restaurant data with optional categories, keywords, and vibes */
  restaurant: Restaurant;
  /** Whether to show the section header */
  showHeader?: boolean;
  /** Learn More tokens still in flight; cuisine chips render inert. See `AtAGlanceProps`. */
  learnMorePending?: boolean;
}

/**
 * Check if restaurant has sufficient custom data for enhanced view.
 * Enhanced = has BusinessConfig with 1+ category AND (3+ keywords OR 1+ toggles)
 */
function hasEnhancedData(restaurant: Restaurant): boolean {
  const hasCategories = restaurant.categories && restaurant.categories.length >= 1;
  const hasKeywords = restaurant.keywords && restaurant.keywords.length >= 3;
  const hasToggles = restaurant.toggles && restaurant.toggles.length > 0;
  return Boolean(hasCategories && (hasKeywords || hasToggles));
}

/**
 * Whether `RestaurantAboutSection` has anything real to show for this
 * restaurant — either the enhanced business-config view (categories,
 * cuisines, highlights, toggle/keyword groups) or the basic vibes fallback.
 * Callers use this to decide whether to render the section (and its
 * surrounding card chrome) at all, rather than letting it fall through to
 * an empty card — a restaurant with neither enhanced data nor vibes has
 * nothing to show (paire-app#121).
 */
export function hasAboutContent(restaurant: Restaurant): boolean {
  if (hasEnhancedData(restaurant)) return true;
  return (restaurant.vibes?.length ?? 0) > 0;
}

// =============================================================================
// Consolidated About Section (enhanced view)
// =============================================================================

/**
 * Renders the consolidated About section for verified businesses.
 * Collapsed: AtAGlance (categories, cuisines, highlights).
 * Expanded: Unified toggle + keyword groups from business config.
 */
function ConsolidatedAboutSection({
  restaurant,
  showHeader,
  learnMorePending,
}: {
  restaurant: Restaurant;
  showHeader: boolean;
  learnMorePending: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { data: uiMetadata } = useUIMetadata();

  // Build toggle items grouped by toggle group key
  const togglesByGroup = useMemo(() => {
    const toggles = restaurant?.toggles;
    if (!toggles || toggles.length === 0) return {};

    const grouped: Record<string, TagItem[]> = {};

    for (const toggle of toggles) {
      if (!toggle.group) continue;

      if (!grouped[toggle.group]) {
        grouped[toggle.group] = [];
      }

      grouped[toggle.group].push({
        key: toggle.key,
        label: getToggleLabel(toggle),
        iconName: toggle.icon,
        group: toggle.group,
      });
    }

    return grouped;
  }, [restaurant?.toggles]);

  // Build keyword items grouped by keyword group key (from business config)
  const keywordsByGroup = useMemo(() => {
    const keywords = restaurant?.keywords ?? [];
    if (keywords.length === 0) return {};

    // Derive keyword group keys from UNIFIED_SECTION_GROUPS to keep a single source of truth
    const allGroupKeys = UNIFIED_SECTION_GROUPS
      .flatMap(g => g.sections)
      .filter(s => s.source === 'keywords')
      .map(s => s.key);

    const tags: Record<string, TagItem[]> = {};

    for (const groupKey of allGroupKeys) {
      const groupKeywordKeys = getKeywordsByGroup(groupKey, uiMetadata);
      // Allergy keywords get their own badge grid (AllergyAccommodations,
      // paire-app#128), so they're left out here to avoid showing them twice.
      const groupKeywords = keywords.filter(
        kw => groupKeywordKeys.includes(kw) && !ALLERGY_ACCOMMODATION_KEYS.has(kw),
      );

      if (groupKeywords.length > 0) {
        tags[groupKey] = groupKeywords.map(kw => {
          const option = getKeywordOption(kw, uiMetadata);
          return {
            key: kw,
            label: getKeywordDisplayName(kw, uiMetadata),
            iconName: option?.icon,
            group: groupKey,
          };
        });
      }
    }

    return tags;
  }, [restaurant?.keywords, uiMetadata]);

  // Determine if any section in the expanded groups has data
  const hasExpandedContent = useMemo(() => {
    for (const group of UNIFIED_SECTION_GROUPS) {
      for (const section of group.sections) {
        if (section.source === 'toggles' && togglesByGroup[section.key]?.length > 0) {
          return true;
        }
        if (section.source === 'keywords' && keywordsByGroup[section.key]?.length > 0) {
          return true;
        }
      }
    }
    return false;
  }, [togglesByGroup, keywordsByGroup]);

  /**
   * Render a row of chips. No `iconColor` override — UnifiedChip's own
   * per-variant default (`--card-text`, mode-paired) is correct here since
   * every chip in this section renders inside the `--card-bg` Box wrapping
   * RestaurantAboutSection (see RestaurantProfile.tsx `cardSurfaceProps`).
   * A hardcoded white iconColor literal used to make icons vanish against
   * the light-mode card background.
   */
  const renderChips = (tags: TagItem[], variant: 'keyword' | 'toggle') => (
    <HStack spacing={2} flexWrap="wrap">
      {tags.map((tag) => (
        <UnifiedChip
          key={tag.key}
          variant={variant}
          label={tag.label}
          group={tag.group}
          icon={getIconComponent(tag.iconName)}
        />
      ))}
    </HStack>
  );

  /** Render a single section (toggle or keyword) with its label */
  const renderSection = (section: SectionConfig) => {
    const isToggle = section.source === 'toggles';
    const tags = isToggle ? togglesByGroup[section.key] : keywordsByGroup[section.key];
    if (!tags || tags.length === 0) return null;

    return (
      <Box key={section.key}>
        <Text
          fontSize="xs"
          fontWeight="semibold"
          color="var(--card-text)"
          textTransform="uppercase"
          letterSpacing="wide"
          mb={2}
        >
          {section.label}
        </Text>
        {renderChips(tags, isToggle ? 'toggle' : 'keyword')}
      </Box>
    );
  };

  return (
    <MotionBox
      variants={sectionVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-50px' }}
    >
      {/* Section header — matches the shared ModuleLabel pattern used by
          every other card-classed section (D45b), instead of a hand-rolled
          heading (layout audit, paire-app#131). Kept outside the spacing={4}
          VStack below: ModuleLabel carries its own mb={3}, and nesting it
          inside a gapped VStack doubles the header-to-content gap (the same
          bug fixed in RestaurantNotes.tsx by this same audit). */}
      {showHeader && (
        <ModuleLabel label="At a Glance" />
      )}

      <VStack spacing={4} align="stretch">
        {/* At A Glance summary (categories, cuisine, highlights) */}
        <AtAGlance restaurant={restaurant} learnMorePending={learnMorePending} />

        {/* Expand/collapse toggle */}
        {hasExpandedContent && (
          <HStack
            as="button"
            spacing={1}
            onClick={() => setIsExpanded(!isExpanded)}
            cursor="pointer"
            py={1}
            _hover={{ opacity: 0.7 }}
          >
            <Text fontSize="sm" fontWeight={600} color="var(--card-link)">
              {isExpanded ? 'Show less' : 'More details'}
            </Text>
            <MotionBox
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown size={16} color="var(--card-link)" />
            </MotionBox>
          </HStack>
        )}

        {/* Expanded unified sections */}
        <Collapse in={isExpanded} animateOpacity>
          <VStack spacing={6} align="stretch">
            {UNIFIED_SECTION_GROUPS.map((group, groupIndex) => {
              // Render each section in the group, filter out empty
              const renderedSections = group.sections
                .map(section => renderSection(section))
                .filter(Boolean);

              if (renderedSections.length === 0) return null;

              return (
                <VStack key={groupIndex} spacing={3} align="stretch">
                  {renderedSections}
                </VStack>
              );
            })}
          </VStack>
        </Collapse>
      </VStack>
    </MotionBox>
  );
}

// =============================================================================
// Basic About Section (vibes fallback)
// =============================================================================

/**
 * Renders the basic fallback About section.
 * Shows calculated vibes when no business-configured data is available.
 */
function BasicAboutSection({
  restaurant,
  showHeader,
}: {
  restaurant: Restaurant;
  showHeader: boolean;
}) {
  const vibes = restaurant.vibes || [];
  const { data: uiMetadata } = useUIMetadata();

  const displayVibes = vibes.slice(0, 5);

  return (
    <MotionBox
      variants={sectionVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-50px' }}
    >
      {/* Section header — see UnifiedAboutSection above for why ModuleLabel
          sits outside the spacing VStack (layout audit, paire-app#131). */}
      {showHeader && (
        <ModuleLabel label="At a Glance" />
      )}

      <VStack spacing={3} align="stretch">
        {/* Vibes as UnifiedChip with DB gradients */}
        <MotionBox
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          display="flex"
          flexWrap="wrap"
          gap={2}
        >
          {displayVibes.map(vibeKey => {
            const vibeGradient = getVibeGradient(vibeKey, uiMetadata);
            const customGradient = vibeGradient
              ? `linear-gradient(135deg, ${vibeGradient[0]}, ${vibeGradient[1]})`
              : undefined;
            return (
              <MotionBox key={vibeKey} variants={vibeVariants}>
                <UnifiedChip
                  variant="vibe"
                  label={getVibeLabel(vibeKey, uiMetadata)}
                  flagEmoji={undefined}
                  customGradient={customGradient}
                />
              </MotionBox>
            );
          })}
        </MotionBox>
      </VStack>
    </MotionBox>
  );
}

// =============================================================================
// Main Export
// =============================================================================

/**
 * Restaurant About Section with progressive enhancement.
 * Automatically selects enhanced or basic view based on available data.
 */
export function RestaurantAboutSection({
  restaurant,
  showHeader = true,
  learnMorePending = false,
}: RestaurantAboutSectionProps) {
  // Callers gate their surrounding card chrome on `hasAboutContent`; this
  // guard keeps the component itself from rendering empty when called directly.
  if (!restaurant || !hasAboutContent(restaurant)) {
    return null;
  }

  const isEnhanced = hasEnhancedData(restaurant);

  if (isEnhanced) {
    return (
      <ConsolidatedAboutSection
        restaurant={restaurant}
        showHeader={showHeader}
        learnMorePending={learnMorePending}
      />
    );
  }

  return (
    <BasicAboutSection
      restaurant={restaurant}
      showHeader={showHeader}
    />
  );
}

export default RestaurantAboutSection;
